import { AnyAction } from 'redux';
import { STATE } from 'app/constants';
import { ApplicationState } from 'app/types-store';
import { ActionDispatching, LoadOptions } from 'app/types-shared';
import { ServerValidationErrors, complexValidationFromServer } from 'app/infrastructure/data/validation';
import { ErrorNotification, SuccessNotification } from 'app/infrastructure/notifications/user-messages';
import * as navigate from 'app/area/shared/navigation';
import { isForbiddenStatus, blockAccess, isNotFoundStatus } from 'app/area/shared/actions-utils';
import { mapExternalUrlGroupFromWire, mapExternalUrlGroupToWire } from './utils/map-external-url';
import { externalUrlGroupsTranslation } from '../constants';
import {
  RequestLanguageOptions, ReceiveLanguageOptions, FailLanguageOptions,
  RequestExternalUrlGroup, ReceiveExternalUrlGroup, FailExternalUrlGroup,
  RequestDeleteExternalUrlGroup, ReceiveDeleteExternalUrlGroup, FailDeleteExternalUrlGroup,
  RequestSaveExternalUrlGroup, ReceiveSaveExternalUrlGroup, FailSaveExternalUrlGroup,
  UpdateItem, ChangeLanguage, CopyLanguageToAll, ClearForm, UpdateValidation, ChangeLongDescription,
  CopyLongDescriptionToAll, UpdateAccessPolicyItem, UpdateAccessPolicyValidation, RemoveAccessPolicy,
  RemoveAllAccessPolicies, ResetAllAccessPolicyValidation, AddAccessPolicy, ResetAccessPolicyClientValidation,
  ResetAccessPolicyServerValidation, RequestAddExternalUrls, ReceiveAddExternalUrls, FailAddExternalUrls,
  UpdateExternalUrlItem, DeleteExternalUrl, DeleteAllExternalUrls, UpdateExternalUrlsValidation,
  RequestExternalUrlOptions, ReceiveExternalUrlOptions, FailExternalUrlOptions
} from './action-types';
import { api } from './api';
import { getBackRoute } from './route';
import {
  ExternalUrlGroupItem, ExternalUrlGroupsItemValidation,
  ExternalUrlsValidation, ExternalUrlValidation, AccessPolicyValue,
  ExternalUrlItem
} from './types-store';
import { AddExternalUrlType, ExternalUrlGroupsActionType } from 'app/area/external-url-groups/form/types';
import { replaceTemplateString } from 'app/infrastructure/platform';
import { ValidationMessage } from 'ui';

import { normalizeSequence } from './utils/normalize-sequence';
import { mapExternalUrlsValidation } from 'app/area/external-url-groups/form/utils/map-external-urls-validation';
import { mapToOptionsRequestParams } from 'app/infrastructure/data/codes';
import { externalUrlItemToStoreValue } from 'app/area/external-url-groups/form/utils/map-external-url-options';

export const actionCreators = {

  loadLanguageOptions: (): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      dispatch(new RequestLanguageOptions());

      try {

        const languageOptions = await api.loadLanguageOptions();

        dispatch(new ReceiveLanguageOptions(
          languageOptions.map(option => ({
            value: option.v,
            description: option.d
          }))
        ));
      } catch (err) {

        if (err && (err.userMessages || err.message)) {
          dispatch(new ErrorNotification(err.userMessages || err.message));
        }
        dispatch(new FailLanguageOptions());
      }
    },

  loadExternalUrlOptions: (loadOptionsParams: LoadOptions): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      const query = loadOptionsParams && loadOptionsParams.query || '';

      dispatch(new RequestExternalUrlOptions(query, loadOptionsParams));

      try {
        const response = await api.loadExternalUrlOptions(query, mapToOptionsRequestParams(loadOptionsParams));
        dispatch(new ReceiveExternalUrlOptions(query, await response.map(externalUrlItemToStoreValue)));
      } catch (err) {

        err && err.userMessages && dispatch(new ErrorNotification(err.userMessages));
        dispatch(new FailExternalUrlOptions(query));
      }
    },

  createExternalUrl: (): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => dispatch(new ReceiveExternalUrlGroup(mapExternalUrlGroupFromWire({}))),

  loadExternalUrlGroup: (id: number): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      dispatch(new RequestExternalUrlGroup());

      try {

        const externalUrlGroupWired = await api.loadExternalUrlGroup(id);
        const externalUrlGroupMapped = mapExternalUrlGroupFromWire(externalUrlGroupWired);

        dispatch(new ReceiveExternalUrlGroup(externalUrlGroupMapped));

      } catch (err) {

        if (isForbiddenStatus(err)) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.externalUrlGroups,
            translationId: externalUrlGroupsTranslation.no_access_to_external_urls
          });
        } else {

          const errorMessage = err && (err.userMessages || err.message);
          !!errorMessage && dispatch(new ErrorNotification(errorMessage));

          navigate.to({ pathname: getBackRoute() });
        }

        dispatch(new FailExternalUrlGroup());
      }
    },

  deleteExternalUrlGroup: (id: number, externalUrlText: string, successMessage: string): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      dispatch(new RequestDeleteExternalUrlGroup());

      try {
        await api.deleteExternalUrlGroup(id);

        navigate.to({ pathname: getBackRoute() }, { state: { internal: true } });

        dispatch(new ReceiveDeleteExternalUrlGroup());
        dispatch(new SuccessNotification(replaceTemplateString(successMessage, externalUrlText)));
      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
        dispatch(new FailDeleteExternalUrlGroup());

        if (isNotFoundStatus(err)) {
          navigate.to({ pathname: getBackRoute() }, { state: { internal: true } });
        }
      }
    },

  saveExternalUrlGroup: (
    actionType: ExternalUrlGroupsActionType,
    externalUrlGroupItem: ExternalUrlGroupItem,
    successMessage: string,
    validationErrorMessage: string
  ): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      const id = externalUrlGroupItem?.id;

      dispatch(new RequestSaveExternalUrlGroup());

      try {
        const externalUrlWire = mapExternalUrlGroupToWire(externalUrlGroupItem);

        if (actionType === ExternalUrlGroupsActionType.edit) {
          await api.updateExternalUrlGroup(id, externalUrlWire);
        } else {
          await api.addExternalUrlGroup(externalUrlWire);
        }

        dispatch(new ReceiveSaveExternalUrlGroup());

        navigate.to({ pathname: getBackRoute() }, { state: { internal: true } });
        dispatch(new SuccessNotification(successMessage));

      } catch (err) {
        if (Object.keys(err?.validationErrors ?? {}).length > 0) {
          const { caseType, code, ...validation } = err.validationErrors;
          dispatch(new ErrorNotification(validationErrorMessage));
          dispatch(new UpdateValidation(complexValidationFromServer(validation)));
          actionCreators.updateAccessPolicyValidation(caseType, externalUrlGroupItem.accessPolicy)(dispatch, getState);
          actionCreators.updateExternalUrlsValidation(mapExternalUrlsValidation({ code }))(dispatch, getState);
        }

        if (err?.userMessages?.length ?? 0 > 0) {
          dispatch(new ErrorNotification(err.userMessages));
        }

        dispatch(new FailSaveExternalUrlGroup());

        if (isNotFoundStatus(err)) {
          navigate.to({ pathname: getBackRoute() }, { state: { internal: true } });
        }
      }
    },

  updateAccessPolicyItem: (accessPolicyValue: AccessPolicyValue): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new UpdateAccessPolicyItem(accessPolicyValue));
    },

  updateAccessPolicyValidation: (
    messages: ValidationMessage[],
    accessPolicyValue: AccessPolicyValue[]
  ): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      const accessPolicyValidation = messages?.map(item => {
        return accessPolicyValue.filter(value =>
          value.caseType.value === item?.parameters?.caseType
          && value.operation.value === item?.parameters?.operation
        );
      }).reduce((obj, item) => obj.concat(item), []);

      accessPolicyValidation?.forEach(accessPolicy => {
        dispatch(new UpdateAccessPolicyValidation(
          accessPolicy.id,
          complexValidationFromServer({ caseType: messages } as ServerValidationErrors))
        );
      });
    },

  removeAccessPolicy: (id: number): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new RemoveAccessPolicy(id));
    },

  removeAllAccessPolicy: (): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new RemoveAllAccessPolicies());
      dispatch(new ResetAllAccessPolicyValidation());
    },

  addAccessPolicy: (): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new AddAccessPolicy());
    },

  resetAccessPolicyClientValidation: (id: number, key?: keyof AccessPolicyValue): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch: any, getState: any) => {
      dispatch(new ResetAccessPolicyClientValidation(id, key));
    },

  resetAccessPolicyServerValidation: (id: number): ActionDispatching<AnyAction, ApplicationState> => (dispatch: any, getState: any) => {
    dispatch(new ResetAccessPolicyServerValidation(id));
  },

  addExternalUrls: (
    addType: AddExternalUrlType,
    externalUrls: ExternalUrlItem[],
    sequence: number
  ): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      try {

        dispatch(new RequestAddExternalUrls());
        dispatch(new ReceiveAddExternalUrls(addType, externalUrls, sequence, normalizeSequence));

      } catch (err) {

        const errorMessage = err && (err.userMessages || err.message);
        !!errorMessage && dispatch(new ErrorNotification(errorMessage));

        dispatch(new FailAddExternalUrls());
      }
    },

  updateExternalUrl: (
    id: number,
    externalUrlItem: ExternalUrlItem,
    validation: ExternalUrlValidation
  ): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new UpdateExternalUrlItem(id, externalUrlItem, validation, normalizeSequence));
    },

  deleteExternalUrl: (externalUrlId: number): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new DeleteExternalUrl(externalUrlId, normalizeSequence));
    },

  deleteAllExternalUrls: (): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new DeleteAllExternalUrls());
    },

  updateExternalUrlsValidation: (
    validation: ExternalUrlsValidation
  ): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new UpdateExternalUrlsValidation(validation));
    },

  updateItem: (item: Partial<ExternalUrlGroupItem>): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new UpdateItem(item));
    },

  changeLanguage: (languageId: number, text: string): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ChangeLanguage(languageId, text));
    },

  copyLanguageToAll: (languageId: number, successMessage: string): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new CopyLanguageToAll(languageId));
      dispatch(new SuccessNotification(successMessage));
    },

  changeLongDescription: (languageId: number, text: string): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ChangeLongDescription(languageId, text));
    },

  copyLongDescriptionToAll: (languageId: number, successMessage: string): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new CopyLongDescriptionToAll(languageId));
      dispatch(new SuccessNotification(successMessage));
    },

  updateValidation: (validation: ExternalUrlGroupsItemValidation): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new UpdateValidation(validation));
    },

  clearForm: (): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ClearForm());
    },

  displayError: (errorMessage: string): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ErrorNotification(errorMessage));
    }
};
